import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Clock, LayoutGrid } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSearchSuggestions } from '../../../hooks/useSearchSuggestions';
import { ProductCard } from '../../../core/components/ProductCard';

export const NavbarSearch = () => {
    const { t, i18n } = useTranslation('shop');
    const navigate = useNavigate();
    const isRTL = i18n.language === 'ar';

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Load recent searches
    useEffect(() => {
        const stored = localStorage.getItem('elraay_recent_searches');
        if (stored) {
            try { setRecentSearches(JSON.parse(stored).slice(0, 5)); } catch (e) { }
        }
    }, []);

    // Debounce
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    // Fetch suggestions
    const { data: suggestions, isLoading } = useSearchSuggestions(debouncedQuery);

    // Dropdown visibility
    const showDropdown = isFocused && (query.length >= 2 || recentSearches.length > 0);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const saveRecentSearch = (term: string) => {
        if (!term.trim()) return;
        const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
        setRecentSearches(updated);
        localStorage.setItem('elraay_recent_searches', JSON.stringify(updated));
    };

    const handleSearchSubmit = (term: string) => {
        if (!term.trim()) return;
        saveRecentSearch(term.trim());
        setIsFocused(false);
        navigate(`/search?q=${encodeURIComponent(term.trim())}`);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) {
            if (e.key === 'Enter') handleSearchSubmit(query);
            return;
        }

        // Calculate total navigable items
        let totalItems = 0;
        if (query.length < 2 && recentSearches.length > 0) {
            totalItems = recentSearches.length;
        } else if (suggestions) {
            totalItems = (suggestions.products?.length || 0) +
                (suggestions.categories?.length || 0) +
                (suggestions.brands?.length || 0) + 1; // +1 for "View all results"
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
        } else if (e.key === 'Escape') {
            setIsFocused(false);
            inputRef.current?.blur();
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex === -1) {
                handleSearchSubmit(query);
                return;
            }

            // Route based on selected index
            let currentIndex = 0;
            if (query.length < 2) {
                const term = recentSearches[selectedIndex];
                if (term) handleSearchSubmit(term);
                return;
            }

            if (suggestions) {
                // View all
                if (selectedIndex === 0) {
                    handleSearchSubmit(query);
                    return;
                }
                currentIndex++;

                const { products, categories, brands } = suggestions;
                if (categories?.length) {
                    for (const cat of categories) {
                        if (currentIndex === selectedIndex) {
                            navigate(`/category/${cat.slug}`);
                            setIsFocused(false);
                            return;
                        }
                        currentIndex++;
                    }
                }
                if (brands?.length) {
                    for (const brand of brands) {
                        if (currentIndex === selectedIndex) {
                            navigate(`/brand/${brand.slug}`);
                            setIsFocused(false);
                            return;
                        }
                        currentIndex++;
                    }
                }
                if (products?.length) {
                    for (const prod of products) {
                        if (currentIndex === selectedIndex) {
                            saveRecentSearch(query);
                            navigate(`/product/${prod.slug}`);
                            setIsFocused(false);
                            return;
                        }
                        currentIndex++;
                    }
                }
            }
        }
    };

    // Helper for highlight
    const highlightMatch = (text: string, match: string) => {
        if (!match.trim()) return text;
        const regex = new RegExp(`(${match})`, 'gi');
        const parts = text.split(regex);
        return parts.map((part, i) =>
            regex.test(part) ? <strong key={i} className="text-gray-900">{part}</strong> : part
        );
    };

    return (
        <div className="relative w-full group" ref={containerRef}>
            <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setSelectedIndex(-1);
                }}
                onFocus={() => setIsFocused(true)}
                onKeyDown={handleKeyDown}
                placeholder={t('search.searchPlaceholder', 'What are you looking for?')}
                className="w-full py-2.5 lg:py-3 ps-5 lg:ps-6 pe-10 lg:pe-12 bg-surface/50 border border-secondary/10 rounded-xl lg:rounded-full text-sm lg:text-base focus:outline-none focus:border-primary/50 focus:bg-white focus:shadow-md transition-all duration-300"
            />
            {query && (
                <button
                    onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                    className="absolute end-10 lg:end-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors bg-white px-1"
                >
                    <X size={16} />
                </button>
            )}
            <button
                onClick={() => handleSearchSubmit(query)}
                className="absolute end-3 lg:end-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-primary transition-colors"
            >
                <Search size={18} className="stroke-[1.5] lg:w-5 lg:h-5" />
            </button>

            {/* Dropdown UI */}
            {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-secondary/10 overflow-hidden z-50 transform origin-top animate-fade-in-up">

                    {/* Recent Searches */}
                    {query.length < 2 && recentSearches.length > 0 && (
                        <div className="p-4">
                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t('search.recentSearches', 'Recent Searches')}</h3>
                            <ul className="flex flex-col">
                                {recentSearches.map((term, idx) => (
                                    <li key={idx}>
                                        <button
                                            onClick={() => handleSearchSubmit(term)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${selectedIndex === idx ? 'bg-primary/5 text-primary' : 'hover:bg-gray-50 text-gray-700'}`}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <Clock size={16} className={selectedIndex === idx ? 'text-primary' : 'text-gray-400'} />
                                            <span className="flex-1 text-sm">{term}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Suggestions */}
                    {query.length >= 2 && (
                        <div className="flex flex-col max-h-[70vh] overflow-y-auto custom-scrollbar">
                            {isLoading ? (
                                <div className="p-8 flex justify-center items-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                </div>
                            ) : (!suggestions || (!suggestions.products.length && !suggestions.categories.length && !suggestions.brands.length)) ? (
                                <div className="p-8 text-center text-gray-500">
                                    <Search size={32} className="mx-auto text-gray-300 mb-3" />
                                    <p className="text-sm">{t('search.noResults', 'No results found for')} "{query}"</p>
                                </div>
                            ) : (
                                <div className="p-2 space-y-4">

                                    {/* View all item */}
                                    <button
                                        onClick={() => handleSearchSubmit(query)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-start transition-colors ${selectedIndex === 0 ? 'bg-primary/5 text-primary' : 'hover:bg-gray-50 text-gray-800'}`}
                                    >
                                        <Search size={18} className="text-primary" />
                                        <span className="text-sm font-medium">{t('search.viewAll', 'View all results for')} <span className="font-bold text-gray-900">"{query}"</span></span>
                                    </button>

                                    {/* Categories & Brands section */}
                                    {(suggestions.categories.length > 0 || suggestions.brands.length > 0) && (
                                        <div className="px-2">
                                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">{t('search.categoriesAndBrands', 'Categories & Brands')}</h3>
                                            <div className="flex flex-col gap-1">
                                                {/* Logic to map category/brand indices exactly matching keyboard nav */}
                                                {suggestions.categories.map((cat, i) => {
                                                    const idx = 1 + i;
                                                    return (
                                                        <button
                                                            key={cat.id}
                                                            onClick={() => { navigate(`/category/${cat.slug}`); setIsFocused(false); }}
                                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-start transition-colors ${selectedIndex === idx ? 'bg-primary/5' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center p-1.5 shrink-0">
                                                                {cat.image ? <img src={cat.image} className="w-full h-full object-contain mix-blend-multiply" alt="" /> : <LayoutGrid size={14} className="text-gray-400" />}
                                                            </div>
                                                            <span className="text-sm text-gray-700">{highlightMatch(cat.name, query)}</span>
                                                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-auto">{t('search.category', 'Category')}</span>
                                                        </button>
                                                    );
                                                })}
                                                {suggestions.brands.map((brand, i) => {
                                                    const idx = 1 + suggestions.categories.length + i;
                                                    return (
                                                        <button
                                                            key={brand.id}
                                                            onClick={() => { navigate(`/brand/${brand.slug}`); setIsFocused(false); }}
                                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-start transition-colors ${selectedIndex === idx ? 'bg-primary/5' : 'hover:bg-gray-50'}`}
                                                        >
                                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center p-1.5 shrink-0">
                                                                {brand.image ? <img src={brand.image} className="w-full h-full object-contain mix-blend-multiply" alt="" /> : <LayoutGrid size={14} className="text-gray-400" />}
                                                            </div>
                                                            <span className="text-sm text-gray-700">{highlightMatch(brand.name, query)}</span>
                                                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-auto">{t('search.brand', 'Brand')}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Products Grid */}
                                    {suggestions.products.length > 0 && (
                                        <div className="px-2 pb-2">
                                            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-2 flex justify-between">
                                                {t('search.products', 'Products')}
                                                <span className="text-gray-500 lowercase normal-case text-xs">{suggestions.products.length} {t('search.results', 'results')}</span>
                                            </h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                {suggestions.products.map((prod, i) => {
                                                    const idx = 1 + suggestions.categories.length + suggestions.brands.length + i;
                                                    return (
                                                        <div key={prod.id} className={selectedIndex === idx ? 'ring-2 ring-primary bg-primary/5 rounded-2xl transition-all' : ''}>
                                                            <ProductCard
                                                                item={{ ...prod, image: prod.image ? prod.image : undefined }}
                                                                className="h-full border-none shadow-none bg-gray-50/50 hover:bg-white"
                                                            />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
